/**
 * Shared PowerSync facade types — implemented identically by the Electron
 * IPC proxy (electronDb.ts) and a thin pass-through wrapper around the real
 * @powersync/web instance (database.ts), so every other renderer module
 * that touches `db` works unchanged regardless of which platform is active.
 */

import type { RawPowerSyncStatus } from '@/renderer/types/powersync';

export interface PowerSyncQueryResultRows {
  _array: unknown[];
  length: number;
  item(idx: number): unknown;
}

export interface PowerSyncQueryResultLike {
  rowsAffected: number;
  insertId?: number;
  rows?: PowerSyncQueryResultRows;
}

export interface PowerSyncTransactionFacade {
  execute(sql: string, params?: unknown[]): Promise<PowerSyncQueryResultLike>;
  get<T = Record<string, unknown>>(sql: string, params?: unknown[]): Promise<T>;
}

export interface PowerSyncFacade {
  readonly currentStatus: RawPowerSyncStatus;
  execute(sql: string, params?: unknown[]): Promise<PowerSyncQueryResultLike>;
  get<T = Record<string, unknown>>(sql: string, params?: unknown[]): Promise<T>;
  getAll<T = Record<string, unknown>>(
    sql: string,
    params?: unknown[],
  ): Promise<T[]>;
  getUploadQueueStats(): Promise<{ count: number }>;
  writeTransaction<T>(
    callback: (tx: PowerSyncTransactionFacade) => Promise<T>,
  ): Promise<T>;
  registerListener(listener: {
    statusChanged?: (s: RawPowerSyncStatus) => void;
  }): () => void;
  onChange(
    listener: { onChange: (event: { changedTables: string[] }) => void },
    options?: unknown,
  ): () => void;
  connect(connector?: unknown): Promise<void>;
}

export function toQueryResultRows(array: unknown[]): PowerSyncQueryResultRows {
  return {
    _array: array,
    length: array.length,
    item: (idx: number) => array[idx],
  };
}

// PowerSync's underlying tx.get()/db.get() throws when a query matches zero
// rows instead of returning null — callers that expect an optional row need
// to normalize this themselves. Matches the pattern already used by psGet().
export function isEmptyResultError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err);
  return /no results|result set is empty/i.test(message);
}
